import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { Megaphone, Trash, Copy, LinkSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = ["Google Ads", "Instagram", "Facebook", "WhatsApp", "Influencer", "Referral", "Organic Search", "Direct", "Other"];
const CATS = ["", "Farmhouses", "Resorts", "Banquet Halls", "Function Halls"];
const STATUSES = ["active", "paused", "ended"];
const empty = { name: "", type: "Google Ads", budget: "", target_category: "", target_location: "", start_date: "", end_date: "", status: "active" };

const SITE = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, "");

export default function AdminCampaigns() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const load = () => api.get("/admin/campaigns").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.name) return toast.error("Enter a campaign name.");
    try {
      await api.post("/admin/campaigns", { ...f, budget: Number(f.budget) || 0 });
      toast.success("Campaign created");
      setF(empty);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (c) => { await api.delete(`/admin/campaigns/${c.id}`); load(); };
  const setStatus = async (c, status) => { await api.patch(`/admin/campaigns/${c.id}`, { status }); load(); };

  const trackedUrl = (c) => {
    const base = c.target_category === "Farmhouses" ? "/hyderabad-farmhouses" : c.target_category === "Resorts" ? "/hyderabad-resorts" : c.target_category === "Banquet Halls" ? "/hyderabad-banquet-halls" : c.target_category === "Function Halls" ? "/hyderabad-function-halls" : "/";
    const p = new URLSearchParams({ utm_source: c.utm_source, utm_medium: c.utm_medium, utm_campaign: c.utm_campaign });
    return `${SITE}${base}?${p.toString()}`;
  };
  const copy = (c) => { navigator.clipboard.writeText(trackedUrl(c)); toast.success("Tracking link copied"); };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Campaigns</h1>
      <p className="mt-1 text-sm text-stone-500">Create marketing campaigns and copy UTM-tracked links. Registrations, leads and bookings are attributed automatically.</p>

      <div className="mt-5 rounded-2xl border border-stone-200 bg-white p-6">
        <p className="mb-3 font-heading font-semibold">New campaign</p>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div><Label className="text-xs text-stone-500">Name</Label><Input data-testid="campaign-name" value={f.name} onChange={set("name")} className="mt-1 rounded-xl" placeholder="Hyderabad Farmhouses Google" /></div>
          <div>
            <Label className="text-xs text-stone-500">Type</Label>
            <Select value={f.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="campaign-type" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-stone-500">Budget ₹</Label><Input data-testid="campaign-budget" type="number" value={f.budget} onChange={set("budget")} className="mt-1 rounded-xl" /></div>
          <div>
            <Label className="text-xs text-stone-500">Target category</Label>
            <Select value={f.target_category || "all"} onValueChange={(v) => set("target_category")(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="campaign-category" className="mt-1 rounded-xl"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c || "all"} value={c || "all"}>{c || "All categories"}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-stone-500">Target location</Label><Input value={f.target_location} onChange={set("target_location")} className="mt-1 rounded-xl" placeholder="Moinabad" /></div>
          <div><Label className="text-xs text-stone-500">Start date</Label><Input type="date" value={f.start_date} onChange={set("start_date")} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs text-stone-500">End date</Label><Input type="date" value={f.end_date} onChange={set("end_date")} className="mt-1 rounded-xl" /></div>
        </div>
        <Button data-testid="campaign-create" onClick={create} className="mt-4 rounded-xl bg-primary hover:bg-green-900">Create Campaign</Button>
      </div>

      <div className="mt-6 space-y-3">
        {items === null ? null : items.length === 0 ? (
          <EmptyState icon={Megaphone} title="No campaigns yet" message="Create your first campaign above to start tracking." />
        ) : items.map((c) => (
          <div key={c.id} data-testid={`campaign-${c.id}`} className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-heading font-semibold text-stone-900">{c.name}</p>
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-stone-600">{c.type}</span>
                </div>
                <p className="mt-1 font-mono text-xs text-stone-400">utm_campaign={c.utm_campaign} · utm_source={c.utm_source} · utm_medium={c.utm_medium}</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={c.status} onValueChange={(v) => setStatus(c, v)}>
                  <SelectTrigger data-testid={`campaign-status-${c.id}`} className="h-8 w-28 rounded-full text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <button data-testid={`campaign-copy-${c.id}`} onClick={() => copy(c)} className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1.5 text-xs text-stone-600 hover:bg-stone-50"><Copy size={14} /> Link</button>
                <button data-testid={`campaign-del-${c.id}`} onClick={() => del(c)} className="text-stone-400 hover:text-destructive"><Trash size={16} /></button>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-stone-50 px-3 py-2 text-xs text-stone-500">
              <LinkSimple size={14} className="shrink-0" /><span className="truncate">{trackedUrl(c)}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {[["Registrations", c.metrics?.registrations], ["Owner sign-ups", c.metrics?.owner_registrations], ["Leads", c.metrics?.leads], ["Bookings", c.metrics?.bookings], ["Confirmed", c.metrics?.confirmed_bookings], ["Revenue", c.metrics?.revenue]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-stone-100 bg-white p-2.5 text-center">
                  <p className="font-heading text-lg font-bold text-stone-900">{l === "Revenue" ? `₹${Number(v || 0).toLocaleString("en-IN")}` : (v ?? 0)}</p>
                  <p className="text-[10px] uppercase tracking-wide text-stone-400">{l}</p>
                </div>
              ))}
            </div>
            {c.budget ? (
              <p className="mt-2 text-xs text-stone-400">
                Budget ₹{Number(c.budget).toLocaleString("en-IN")}
                {c.metrics?.leads ? ` · Cost/Lead ₹${Math.round(Number(c.budget) / c.metrics.leads).toLocaleString("en-IN")}` : ""}
                {c.metrics?.confirmed_bookings ? ` · Cost/Booking ₹${Math.round(Number(c.budget) / c.metrics.confirmed_bookings).toLocaleString("en-IN")}` : ""}
                {c.metrics?.revenue ? ` · ROAS ${(c.metrics.revenue / Number(c.budget)).toFixed(2)}x` : ""}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
